import React, { useCallback, useRef } from 'react';
import styled from 'styled-components/native';
import * as Yup from 'yup';
import Colors from '../../constants/Colors';
import { Ionicons } from '@expo/vector-icons';
import { Formik, FormikProps } from 'formik';
import { Modal, TouchableOpacity, ModalProps, ScrollView } from 'react-native';
import Spacer from '../Spacer';
import { H4, H5 } from '../Typography';
import FormButton from '../form/FormButton';
import FormMeasurement from '../form/FormMeasurement';
import {
  OuraSpecificDataType,
  UserAttribute,
  UserAttributeInputType,
  UserAttributeSource,
  UserAttributeType
} from '../../models/UserAttribute';
import { useSaveUserAttributes } from '../../hooks/user/useSaveUserAttributes';
import Form from '../form/Form';
import { addMilliseconds, format } from 'date-fns';
import AlertBox from '../AlertBox';
import HoursField from '../form/HoursField';
import { UserUnitSystem } from '../../models/User';
import { convertToImperial } from '../MyDataList';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';

const StyledDimContainer = styled.View`
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  justify-content: flex-end;
`;

const StyledDialog = styled(KeyboardAvoidingView)`
  width: 100%;
  border-top-right-radius: 32px;
  border-top-left-radius: 32px;
  max-width: 1080px;
  overflow: hidden;
  background-color: ${Colors.background};
  padding: 16px;
  align-self: center;
`;

const StyledContainer = styled.View`
  padding: 20px 0px 30px 0;
`;

const StyledActionSheetTitleContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const StyledTextInputView = styled.View`
  padding: 0 12px 0 12px;
  height: 64px;
  border: 1px solid ${Colors.formInputBorder};
  border-radius: 8px;
  flex-direction: row;
  align-items: center;
  background-color: #fff;
  justify-content: space-between;
`;

export type WearableMeasurement = {
  date: Date;
  userAttribute?: UserAttribute;
};

type EditMeasurementActionSheetProps = ModalProps & {
  userId: number;
  type: UserAttributeType | OuraSpecificDataType;
  source: UserAttributeSource;
  sourceName?: string;
  data: WearableMeasurement;
  unit?: string;
  unitSystem?: UserUnitSystem;
  onSaved: () => void;
};

const EditMeasurementActionSheet: React.FC<EditMeasurementActionSheetProps> = ({
  userId,
  type,
  source,
  sourceName,
  data,
  unit,
  visible,
  unitSystem,
  onSaved,
  onRequestClose
}) => {
  const formikRef = useRef<FormikProps<any> | null>();
  const { date, userAttribute } = data;
  const { saveUserAttributesError, saveUserAttributes } =
    useSaveUserAttributes();

  // convert imperial to metric
  const convertToMetric: (value: number, type?: string) => number = (
    value,
    type
  ) => {
    if (type) {
      let conversion: 'fahrenheit' | 'pound' | 'feet' | undefined;
      switch (type) {
        case '°F':
          conversion = 'fahrenheit';
          break;
        case 'lb':
          conversion = 'pound';
          break;
        case 'ft':
          conversion = 'feet';
          break;
      }
      return value
        ? conversion === 'pound'
          ? value * 0.45359237
          : conversion === 'fahrenheit'
          ? (value - 32) * (5 / 9)
          : conversion === 'feet'
          ? value * 30.48
          : value
        : value;
    }
    return value;
  };

  const convertUnit = (unit?: string) => {
    switch (unit) {
      case '°F':
        return '°C';
      case 'lb':
        return 'kg';
      case 'ft':
        return 'cm';
      default:
        return unit;
    }
  };

  const handleSubmit = useCallback(
    async (value?: number, systolic?: number, diastolic?: number) => {
      formikRef?.current?.setSubmitting(true);
      let id: number | undefined;
      let startDate = date;
      if (
        userAttribute &&
        userAttribute.inputType === UserAttributeInputType.Manual
      ) {
        id = userAttribute.id;
      } else if (userAttribute?.startDate) {
        startDate = addMilliseconds(userAttribute.startDate, 1);
      }
      const endDate = new Date(userAttribute?.endDate || date);
      endDate.setHours(17, 59, 59, 59);
      try {
        await saveUserAttributes(userId, [
          {
            id,
            userId,
            type,
            source,
            jsonValue: { sourceName },
            unit: convertUnit(unit),
            startDate,
            endDate,
            textValue:
              userAttribute?.type === UserAttributeType.BloodPressure ||
              unit === 'mmHg'
                ? `${systolic}/${diastolic}`
                : null,
            numberValue:
              userAttribute?.type === UserAttributeType.BloodPressure ||
              unit === 'mmHg'
                ? null
                : unitSystem === UserUnitSystem.Imperial &&
                  typeof value !== 'undefined'
                ? convertToMetric(value, unit)
                : value,
            inputType: UserAttributeInputType.Manual
          } as UserAttribute
        ]);
        formikRef?.current?.setSubmitting(false);
        onSaved();
      } catch (e: any) {
        formikRef?.current?.setSubmitting(false);
      }
    },
    [
      date,
      onSaved,
      saveUserAttributes,
      source,
      sourceName,
      type,
      unit,
      userAttribute,
      userId
    ]
  );

  const limitDecimal = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: unit === '°F' || unit === '°C' ? 1 : 2
  }).format;

  return (
    <Modal
      statusBarTranslucent
      animationType='fade'
      transparent
      visible={visible}
    >
      <StyledDimContainer>
        <StyledDialog behavior='padding'>
          <ScrollView keyboardShouldPersistTaps='handled'>
            <Formik
              innerRef={formik => (formikRef.current = formik)}
              initialValues={{
                value: `${
                  userAttribute?.numberValue
                    ? limitDecimal(
                        unitSystem === UserUnitSystem.Imperial
                          ? convertToImperial(userAttribute.numberValue, unit)
                          : userAttribute.numberValue
                      )
                    : ''
                }`,
                systolic:
                  userAttribute?.type === UserAttributeType.BloodPressure
                    ? userAttribute.textValue
                      ? userAttribute.textValue.split('/')[0]
                      : undefined
                    : undefined,
                diastolic:
                  userAttribute?.type === UserAttributeType.BloodPressure
                    ? userAttribute.textValue
                      ? userAttribute.textValue.split('/')[1]
                      : undefined
                    : undefined
              }}
              validationSchema={Yup.object().shape(
                userAttribute?.type === UserAttributeType.BloodPressure ||
                  unit === 'mmHg'
                  ? {
                      systolic: Yup.number().required(),
                      diastolic: Yup.number().required()
                    }
                  : {
                      value: Yup.number().required()
                    }
              )}
              onSubmit={({ value, systolic, diastolic }) =>
                handleSubmit(+value, Number(systolic), Number(diastolic))
              }
            >
              <Form>
                <StyledContainer>
                  <StyledActionSheetTitleContainer>
                    <H4 color='dark'>Edit Measurements</H4>
                    <TouchableOpacity onPress={e => onRequestClose?.(e)}>
                      <Ionicons name='close' size={34} />
                    </TouchableOpacity>
                  </StyledActionSheetTitleContainer>
                  <Spacer size={5} />
                  <StyledTextInputView>
                    <H5 color='#615E6D'>Date</H5>
                    <H5 color='dark'>{format(date, 'MMM d')}</H5>
                  </StyledTextInputView>
                  <Spacer size='large' />
                  {type === UserAttributeType.Sleep ? (
                    <HoursField
                      value={userAttribute?.numberValue}
                      label={type}
                      onChange={value => {
                        formikRef.current?.setFieldValue('value', value);
                        formikRef.current?.handleChange('value');
                      }}
                    />
                  ) : type === UserAttributeType.BloodPressure ? (
                    <>
                      <FormMeasurement
                        name='systolic'
                        label={'Systolic'}
                        precision={15}
                        scale={5}
                      />
                      <FormMeasurement
                        name='diastolic'
                        label={'Diastolic'}
                        precision={15}
                        scale={5}
                      />
                    </>
                  ) : (
                    <FormMeasurement
                      name='value'
                      label={type}
                      unit={unit}
                      precision={15}
                      scale={5}
                    />
                  )}
                  {saveUserAttributesError ? (
                    <>
                      <AlertBox severity='error'>
                        {saveUserAttributesError.message}
                      </AlertBox>
                      <Spacer size={2} />
                    </>
                  ) : (
                    <Spacer size={8} />
                  )}
                  <FormButton>Save</FormButton>
                </StyledContainer>
              </Form>
            </Formik>
          </ScrollView>
        </StyledDialog>
      </StyledDimContainer>
    </Modal>
  );
};

export default EditMeasurementActionSheet;
